import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  useCreateDevice,
  useDeviceCredentialsPrompt,
} from "@/features/devices/hooks/use-devices";
import { useClientId } from "@/features/utils/api-scope";
import { useTranslation } from "@/features/utils/i18n";
import { Plus } from "@phosphor-icons/react";
import { useState } from "react";
import { toast } from "sonner";
import {
  DeviceForm,
  emptyDeviceForm,
  toDeviceInput,
  type DeviceFormData,
} from "./device-form";

export const CreateDeviceDialog = () => {
  const { t } = useTranslation("devices");
  const clientId = useClientId();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<DeviceFormData>(emptyDeviceForm());

  // The credentials dialog is mounted by the page, so closing this one cannot
  // take it down with it.
  const { showIssued } = useDeviceCredentialsPrompt();

  const createDevice = useCreateDevice();

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientId) return;

    try {
      const created = await createDevice.mutateAsync({
        clientId,
        data: toDeviceInput(form),
      });
      setOpen(false);
      setForm(emptyDeviceForm());
      showIssued(created.device.id!, created.credentials);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("devices.messages.createFailed"),
      );
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            {t("devices.actions.add")}
          </Button>
        </DialogTrigger>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <form onSubmit={handleCreate}>
            <DialogHeader>
              <DialogTitle>{t("devices.create.title")}</DialogTitle>
              <DialogDescription>
                {t("devices.create.description")}
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              <DeviceForm value={form} onChange={setForm} creating />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
              >
                {t("devices.actions.cancel")}
              </Button>
              <Button type="submit" disabled={createDevice.isPending}>
                {t("devices.actions.create")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
